import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const EXERCISE_LABELS = {
  mood_tracking: "exercise.mood",
  emotion_register: "exercise.emotion_register",
  gratitude: "exercise.gratitude",
  pomodoro: "exercise.pomodoro",
  meditation: "exercise.meditation",
  cognitive_defusion: "exercise.cognitive_defusion",
  self_compassion: "exercise.self_compassion",
  behavioral_activation: "exercise.behavioral_activation",
  values_exploration: "exercise.values_exploration",
  needs_exploration: "exercise.needs_exploration",
  problem_solving: "exercise.problem_solving",
  qualities_exploration: "exercise.qualities_exploration",
  capabilities_exploration: "exercise.capabilities_exploration",
  assertiveness: "exercise.assertiveness",
  self_reinforcement: "exercise.self_reinforcement",
};

const COLORS = [
  "#e11d48", "#7c3aed", "#f59e0b", "#3b82f6", "#f97316",
  "#06b6d4", "#ec4899", "#10b981", "#6366f1", "#a855f7",
  "#eab308", "#d946ef", "#84cc16", "#14b8a6",
];

const ExerciseProgressChart = () => {
  const { t } = useI18n();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axios.get(`${API}/patient/exercise-progress`);
        setProgress(res.data);
      } catch (error) {
        console.error("Error fetching progress:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-48 bg-muted rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  if (!progress || Object.keys(progress).length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t("patient.exerciseProgress")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">{t("patient.noProgress")}</p>
        </CardContent>
      </Card>
    );
  }

  const chartData = Object.entries(progress).map(([type, data]) => ({
    name: t(EXERCISE_LABELS[type] || type),
    type,
    completed: data.assigned_completed + data.standalone_entries,
    pending: data.assigned_pending,
    total: data.assigned_completed + data.assigned_pending + data.standalone_entries,
  })).sort((a, b) => b.total - a.total);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    return (
      <div className="bg-popover border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-medium mb-1">{label}</p>
        <p className="text-green-600">{t("patient.completed")}: {data?.completed}</p>
        <p className="text-orange-500">{t("patient.assigned")}: {data?.pending}</p>
      </div>
    );
  };

  return (
    <Card data-testid="exercise-progress-chart">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          {t("patient.exerciseProgress")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 40)}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20 }}>
            <XAxis type="number" allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="name"
              width={130}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="completed" stackId="a" radius={[0, 0, 0, 0]} name={t("patient.completed")}>
              {chartData.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Bar>
            <Bar dataKey="pending" stackId="a" fill="#e5e7eb" radius={[0, 4, 4, 0]} name={t("patient.assigned")} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ExerciseProgressChart;
